import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface Props {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  confirmKind?: 'default' | 'destructive';
  /** Skip the reason field — used for actions that don't require one (e.g. requeue). */
  reasonOptional?: boolean;
  onConfirm: (reason: string) => Promise<void> | void;
  onOpenChange: (open: boolean) => void;
}

const REASON_MIN = 3;
const REASON_MAX = 500;

export function ReasonModal({
  open,
  title,
  description,
  confirmLabel,
  confirmKind = 'destructive',
  reasonOptional = false,
  onConfirm,
  onOpenChange,
}: Props) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason('');
      setBusy(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const valid = reasonOptional || (trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX);

  async function submit() {
    if (busy || !valid) {
      return;
    }
    setBusy(true);
    try {
      await onConfirm(trimmed);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {!reasonOptional && (
          <div className="grid gap-2">
            <Label htmlFor="reason" className="text-xs text-muted-foreground">
              Reason (logged in audit, {REASON_MIN}–{REASON_MAX} chars)
            </Label>
            <Textarea
              id="reason"
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you doing this?"
              className="min-h-20"
            />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={confirmKind === 'destructive' ? 'destructive' : 'default'}
            onClick={submit}
            disabled={busy || !valid}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
